/* eslint-disable no-console */
/* eslint no-underscore-dangle: ["error", { "allow": ["__typename"] }] */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// global variables
import {
  userKinds,
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
} from 'config';
// our components
import RouterContext from 'helpers/routerContext';
import qs, { getQueryString } from 'helpers/queryStrings';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import { PageInfo } from 'components/common/Paginator';
import { Query, Mutation } from 'components/common/Query';
import UserAgencyList from './List';

/**
 * Query to fetch users from api
 */
export const AGENCY_USERS_QUERY = gql`
  query UserAgencyList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $isActive: Boolean
    $contactInfo: String
    $userType: [FilterUserType]!
    $agency: ID
  ) {
    adminUsers(
      first: $first
      after: $after
      last: $last
      before: $before
      isActive: $isActive
      userType: $userType
      contactInfo: $contactInfo
      agency: $agency
    ) @connection(key: "users_agency") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          fakeId
          fullname
          email
          phone
          photo {
            gcsKey
            url
          }
          isActive
          ... on UserAgencyType {
            lead
            leadAgency {
              id
              fullname
            }
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to delete users
 */
export const DELETE_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deleteAdminUser(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to block users
 */
export const BLOCK_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deactivateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to activate users
 */
export const ACTIVE_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    activateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * @class
 * @extends Component
 */
class UserAgencyListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;

    const defaultFilters = {
      isActive: undefined,
      agency: undefined,
    };

    this.state = {
      textToSearch: initialFilters.contactInfo || '',
      filters: { ...defaultFilters, ...initFilters },
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };

    this.Compose = adopt({
      mutateUserDelete: this.userDelete,
      mutateUserBlock: this.userBlock,
      mutateUserUnBlock: this.userUnBlock,
      query: this.listUsers,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listUsers = ({ render }) => {
    const { numberOfRowsSelected, filters, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return (
      <Query
        query={AGENCY_USERS_QUERY}
        variables={{
          ...paginationVariables,
          ...filters,
          userType: [userKinds.USER_AGENCY, userKinds.AGENCY_CLIENT],
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation delete callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userDelete = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={DELETE_USERS_MUTATION}
        onCompleted={this.mutationCompleted('deleteAdminUser', itemProcessing)}
        update={(cache, { data: { deleteAdminUser } }) => {
          const data = { status: 'REMOVED', __typename: 'UserAgencyType' };

          bulkUpdate(
            cache,
            'UserAgencyType',
            'Delete',
            'status',
            data,
            deleteAdminUser.successIds,
          );
        }}
      >
        {handleUserDelete => render({ handleUserDelete })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userBlock = ({ render }) => {
    const { itemProcessing, lastVariables, filters } = this.state;
    return (
      <Mutation
        mutation={BLOCK_USERS_MUTATION}
        onCompleted={this.mutationCompleted(
          'deactivateUsers',
          itemProcessing,
          lastVariables,
        )}
        update={(cache, { data: { deactivateUsers } }) => {
          const data = { isActive: false, __typename: 'UserAgencyType' };

          bulkUpdate(
            cache,
            'UserAgencyType',
            'Block',
            'isActive',
            data,
            deactivateUsers.successIds,
          );

          if (filters.isActive === true) {
            const variables = { last: deactivateUsers.successIds.length };
            const { adminUsers } = cache.readQuery({
              query: AGENCY_USERS_QUERY,
            });

            const newEdges = adminUsers.edges.filter(
              edge => deactivateUsers.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: AGENCY_USERS_QUERY,
              data: {
                adminUsers: {
                  ...adminUsers,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleUserBlock => render({ handleUserBlock })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userUnBlock = ({ render }) => {
    const { itemProcessing, lastVariables, filters } = this.state;
    return (
      <Mutation
        mutation={ACTIVE_USERS_MUTATION}
        onCompleted={this.mutationCompleted(
          'activateUsers',
          itemProcessing,
          lastVariables,
        )}
        update={(cache, { data: { activateUsers } }) => {
          const data = { isActive: true, __typename: 'UserAgencyType' };

          bulkUpdate(
            cache,
            'UserAgencyType',
            'UnBlock',
            'isActive',
            data,
            activateUsers.successIds,
          );

          if (filters.isActive === false) {
            const variables = { last: activateUsers.successIds.length };
            const { adminUsers } = cache.readQuery({
              query: AGENCY_USERS_QUERY,
            });

            const newEdges = adminUsers.edges.filter(
              edge => activateUsers.successIds.indexOf(edge.node.id) === -1,
            );

            cache.writeQuery({
              query: AGENCY_USERS_QUERY,
              data: {
                adminUsers: {
                  ...adminUsers,
                  edges: newEdges,
                },
              },
            });

            this.fetchMore({
              variables,
              updateQuery: this.updateQuery,
            });
          }
        }}
      >
        {handleUserUnBlock => render({ handleUserUnBlock })}
      </Mutation>
    );
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters,
      // eslint-disable-next-line react/no-unused-state
      agency: filters.agency === '' ? undefined : filters.agency,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      agency: filters.agency === '' ? undefined : filters.agency,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {string} text - Text to be updated.
   */
  handleTextChanged = text => {
    this.setState({ textToSearch: text });
  };

  /**
   * @description
   * OnComplete mutations.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      showNotification: failedIds.length > 0,
    });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.adminUsers;
    if (newEdges.length !== 0) {
      return {
        adminUsers: {
          __typename: previousResult.adminUsers.__typename,
          edges: [...previousResult.adminUsers.edges, ...newEdges],
          pageInfo,
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render UserList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      textToSearch,
      pagination,
      failedIds,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateUserDelete: { handleUserDelete },
          mutateUserBlock: { handleUserBlock },
          mutateUserUnBlock: { handleUserUnBlock },
          query: { data, loading },
        }) => (
          <UserAgencyList
            data={data}
            appliedFilter={filters}
            paginationDirection={pagination.direction}
            textToSearch={textToSearch}
            textChanged={this.handleTextChanged}
            filter={this.handleFilter}
            userDelete={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserDelete({ variables: { ids: items } });
            }}
            userBlock={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserBlock({ variables: { ids: items } });
            }}
            userUnBlock={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserUnBlock({ variables: { ids: items } });
            }}
            numberOfRowsSelected={numberOfRowsSelected}
            changePage={paginatorVariables => {
              this.handleChangePage(paginatorVariables);
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * UserListContainer PropTypes
 * @param me - prop current logged user
 * @param initialFilters - prop initial Filters to apply
 */

UserAgencyListContainer.propTypes = {
  me: PropTypes.shape({}).isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default UserAgencyListContainer;
